import { parseWorklistParams, WorklistQueryParams } from '@utils/urlQueries'
import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

type ParamValue = string | number | boolean | null | undefined
type ArrayParamValue = string[] | number[]

interface UseUrlStateProps<T> {
  /**
   * Transform raw URL params into typed object
   */
  getObject: (params: Record<string, string>) => T
}

export const useUrlState = <T extends Record<string, any>>({
  getObject,
}: UseUrlStateProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const urlState = useMemo<T>(() => {
    const rawParams = Object.fromEntries(searchParams.entries())
    return getObject(rawParams)
  }, [searchParams, getObject])

  /**
   * Update a single param
   */
  const updateParam = useCallback(
    (key: keyof T, value: ParamValue | ArrayParamValue) => {
      const params = new URLSearchParams(searchParamsRef.current)
      if (value === null || value === undefined || value === '') {
        params.delete(key as string)
      } else if (Array.isArray(value)) {
        params.set(key as string, value.join(','))
      } else {
        params.set(key as string, String(value))
      }
      setSearchParams(params)
    },
    [setSearchParams],
  )

  /**
   * Update multiple params at once
   */
  const updateParams = useCallback(
    (newParams: Partial<Record<keyof T, ParamValue | ArrayParamValue>>) => {
      const params = new URLSearchParams(searchParamsRef.current)

      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          params.delete(key)
        } else if (Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, String(value))
        }
      })

      setSearchParams(params)
    },
    [setSearchParams],
  )

  /**
   * Clear all params from URL
   */
  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams())
  }, [setSearchParams])

  /**
   * Get a single param value with optional type parsing
   */
  const getParam = useCallback(
    (key: keyof T): string | null => {
      return searchParams.get(key as string)
    },
    [searchParams],
  )

  /**
   * Check if URL has any params
   */
  const hasParams = searchParams.toString().length > 0

  return {
    urlState,
    updateParam,
    updateParams,
    clearParams,
    getParam,
    hasParams,
    searchParams,
  }
}

export const useWorklistUrlState = () => {
  const urlState = useUrlState<WorklistQueryParams>({
    getObject: parseWorklistParams,
  })

  // Check if any filter is active
  const hasFilters = !!(
    urlState.urlState?.startDate ||
    urlState.urlState?.endDate ||
    urlState.urlState?.search ||
    urlState.urlState?.siteId ||
    urlState.urlState?.state ||
    urlState.urlState?.patientIds
  )

  return {
    ...urlState,
    hasFilters,
  }
}
